/**
 * Site-admin "Create shared preset" modal.
 *
 * Always submits to POST /group/admin/presets/ so the API uses the chosen
 * owner_contact_id and bypasses owner-side visibility on constituents +
 * managers (cross-cutting #3 in the Phase 2 spec). Audience is forced to
 * "shared" - the admin tab does not create personal presets on behalf of
 * other users.
 *
 * Layout (2026-06 redesign): a wide modal split into a left section-rail
 * (scroll-spy, not a gated wizard) and a right scrolling pane with three
 * sections - Basic details, Recipients, Administration. The rail highlights
 * the section in view and scrolls to a section on click; every field stays
 * editable at all times. The submit body is unchanged from the prior layout.
 *
 * Validation surfaced inline:
 *   - V1 manager_contact_ids requires shared audience (always satisfied
 *     here, but mentioned in copy so the operator understands the form).
 *   - V8 owner_must_be_active (server-side; we relay the structured error).
 *
 * The owner picker reuses the multi-select contact picker in single-select
 * mode by storing one numeric id and emptying the array on each toggle.
 */
import { useEffect, useRef, useState } from "react";
import {
  IonButton,
  IonButtons,
  IonCheckbox,
  IonContent,
  IonFooter,
  IonHeader,
  IonIcon,
  IonInput,
  IonPopover,
  IonTitle,
  IonToolbar,
} from "@ionic/react";
import {
  chevronDownOutline,
  closeOutline,
  documentTextOutline,
  informationCircleOutline,
  lockClosedOutline,
  peopleOutline,
} from "ionicons/icons";
import { displayMsg } from "pymble-ui/components";
import { useCreatePresetAdmin } from "../../hooks/usePresets";
import { ContactPicker } from "./ContactPicker";
import { ConstituentGroupPicker } from "./ConstituentGroupPicker";
import {
  defaultCompanionNameLong,
  defaultCompanionNameShort,
} from "./companionNaming";
import { parsePresetError } from "./presetErrorParser";
import {
  PRESET_NAME_LONG_MAX,
  PRESET_NAME_SHORT_MAX,
  type PresetAdminCreateRequest,
} from "../../types/preset";

import "./presets.css";

interface CreatePresetModalProps {
  onDismiss: () => void;
  onCreated: (presetId: string) => void;
}

interface RailSection {
  id: string;
  step: number;
  icon: string;
  title: string;
  subtitle: string;
}

const SECTIONS: RailSection[] = [
  {
    id: "basic",
    step: 1,
    icon: documentTextOutline,
    title: "Basic details",
    subtitle: "Name your preset and choose the owner",
  },
  {
    id: "recipients",
    step: 2,
    icon: peopleOutline,
    title: "Recipients",
    subtitle: "Choose who should receive this preset",
  },
  {
    id: "administration",
    step: 3,
    icon: lockClosedOutline,
    title: "Administration",
    subtitle: "Managers and privacy settings",
  },
];

export function CreatePresetModal({
  onDismiss,
  onCreated,
}: CreatePresetModalProps) {
  const create = useCreatePresetAdmin();

  const [ownerId, setOwnerId] = useState<number[]>([]);
  const [nameShort, setNameShort] = useState("");
  const [nameLong, setNameLong] = useState("");
  const [constituentIds, setConstituentIds] = useState<string[]>([]);
  const [individualIds, setIndividualIds] = useState<string[]>([]);
  const [managerIds, setManagerIds] = useState<number[]>([]);
  const [withCompanion, setWithCompanion] = useState(false);
  // `null` = never edited, so the field tracks the source name. Any keystroke
  // (including clearing it) pins the operator's value so a later edit to the
  // source name cannot silently overwrite what they typed.
  const [companionNameShort, setCompanionNameShort] = useState<string | null>(
    null
  );
  const [companionNameLong, setCompanionNameLong] = useState<string | null>(
    null
  );
  const [errorTitle, setErrorTitle] = useState<string | null>(null);
  const [errorBullets, setErrorBullets] = useState<string[]>([]);
  const [adminOpen, setAdminOpen] = useState(false);
  const [activeId, setActiveId] = useState<string>(SECTIONS[0].id);

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const sectionRefs = useRef<Record<string, HTMLElement | null>>({});

  // Scroll-spy: mark the section nearest the top of the scroll pane active.
  // Guarded for jsdom/SSR where IntersectionObserver is absent.
  useEffect(() => {
    const root = scrollRef.current;
    if (!root || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        const top = visible[0]?.target as HTMLElement | undefined;
        if (top?.dataset.sectionId) setActiveId(top.dataset.sectionId);
      },
      { root, threshold: [0.2, 0.5, 0.75], rootMargin: "0px 0px -55% 0px" }
    );
    SECTIONS.forEach((s) => {
      const el = sectionRefs.current[s.id];
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const goToSection = (id: string) => {
    setActiveId(id);
    sectionRefs.current[id]?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  const ownerSelected = ownerId.length > 0 ? ownerId[0] : null;

  // The long name falls back to the short one on submit, so the companion's
  // long-name default has to follow the same fallback or the two names would
  // disagree whenever the operator left "Name (long)" blank.
  const effectiveNameLong = nameLong.trim() || nameShort.trim();
  const companionShortValue =
    companionNameShort ?? defaultCompanionNameShort(nameShort);
  const companionLongValue =
    companionNameLong ?? defaultCompanionNameLong(effectiveNameLong);

  const handleOwnerChange = (ids: number[]) => {
    // Single-select: keep only the last id picked. Empty array means cleared.
    if (ids.length === 0) {
      setOwnerId([]);
      return;
    }
    setOwnerId([ids[ids.length - 1]]);
  };

  const handleSubmit = async () => {
    setErrorTitle(null);
    setErrorBullets([]);

    if (!ownerSelected) {
      setErrorTitle("Pick an owner before submitting");
      goToSection("basic");
      return;
    }
    if (!nameShort.trim()) {
      setErrorTitle("Name (short) is required");
      goToSection("basic");
      return;
    }

    const companionShort = companionShortValue.trim();
    if (withCompanion && !companionShort) {
      setErrorTitle("Name the companion parent group, or clear the checkbox");
      goToSection("recipients");
      return;
    }

    const body: PresetAdminCreateRequest = {
      owner_contact_id: ownerSelected,
      name_short: nameShort.trim(),
      name_long: nameLong.trim() || nameShort.trim(),
      audience: "shared",
      definition: {
        constituent_group_ids: constituentIds,
        individual_contact_ids: individualIds,
      },
      manager_contact_ids: managerIds,
      // Companion fields are OMITTED entirely when unticked - the server
      // defaults the flag to false, and an explicit false only adds noise.
      ...(withCompanion
        ? {
            create_companion_parent_group: true,
            companion_name_short: companionShort,
            companion_name_long: companionLongValue.trim() || companionShort,
          }
        : {}),
    };

    try {
      const preset = await create.mutateAsync(body);
      displayMsg(
        preset?.companion_preset_id
          ? "Preset and companion parent group created"
          : "Preset created",
        {
          type: "success",
          position: "top",
          duration: 1500,
        }
      );
      onCreated(preset.id);
    } catch (err) {
      const parsed = parsePresetError(err);
      if (parsed) {
        setErrorTitle(parsed.title);
        setErrorBullets(parsed.bullets);
      } else {
        setErrorTitle("Failed to create preset - please try again");
      }
    }
  };

  const isSubmitting = create.isPending;

  return (
    <>
      <IonHeader>
        <IonToolbar>
          <IonTitle>Create shared preset</IonTitle>
          <IonButtons slot="end">
            <IonButton fill="clear" onClick={onDismiss}>
              Cancel
            </IonButton>
            <IonButton
              fill="clear"
              onClick={onDismiss}
              aria-label="Close"
              className="preset-modal-close"
            >
              <IonIcon slot="icon-only" icon={closeOutline} />
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>

      <IonContent scrollY={false} className="preset-create-content">
        <div className="preset-create-layout">
          <nav className="preset-rail" aria-label="Preset sections">
            {SECTIONS.map((s) => {
              const isActive = activeId === s.id;
              return (
                <button
                  key={s.id}
                  type="button"
                  className={
                    isActive
                      ? "preset-rail-step preset-rail-step--active"
                      : "preset-rail-step"
                  }
                  aria-current={isActive ? "step" : undefined}
                  onClick={() => goToSection(s.id)}
                >
                  <span className="preset-rail-badge">{s.step}</span>
                  <span className="preset-rail-text">
                    <span className="preset-rail-title">
                      <IonIcon icon={s.icon} aria-hidden="true" />
                      {s.title}
                    </span>
                    <span className="preset-rail-subtitle">{s.subtitle}</span>
                  </span>
                </button>
              );
            })}
          </nav>

          <div className="preset-create-sections" ref={scrollRef}>
            {errorTitle && (
              <div className="preset-modal-error" role="alert">
                <strong>{errorTitle}</strong>
                {errorBullets.length > 0 && (
                  <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
                    {errorBullets.map((b, i) => (
                      <li key={i}>{b}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {/* ---- Basic details ---- */}
            <section
              className="preset-section"
              data-section-id="basic"
              ref={(el) => (sectionRefs.current.basic = el)}
            >
              <h2 className="preset-section-title">Basic details</h2>
              <div className="preset-grid-2">
                <div className="preset-col">
                  <div className="preset-modal-field">
                    <span className="preset-modal-field-label">
                      Owner
                      <span className="preset-required" aria-hidden="true">
                        {" "}
                        *
                      </span>
                    </span>
                    <span className="preset-modal-field-hint">
                      The staff member this preset belongs to. The admin
                      endpoint bypasses owner-side visibility on constituents +
                      managers, so you can build a preset for anyone.
                    </span>
                    <ContactPicker
                      mode="numeric"
                      collapsible
                      selectedIds={ownerId}
                      onChange={handleOwnerChange}
                      placeholder="Search staff..."
                      types={["staff"]}
                      unknownLabel="Staff member"
                    />
                  </div>
                </div>

                <div className="preset-col">
                  <div className="preset-modal-field">
                    <span className="preset-modal-field-label">
                      Name (short)
                      <span className="preset-required" aria-hidden="true">
                        {" "}
                        *
                      </span>
                    </span>
                    <span className="preset-modal-field-hint">
                      e.g. 9 Year 11 English Extension
                    </span>
                    <IonInput
                      className="preset-text-input"
                      fill="outline"
                      aria-label="Name (short)"
                      value={nameShort}
                      onIonInput={(e) => setNameShort(e.detail.value ?? "")}
                      placeholder="e.g. 9 Year 11 English Extension"
                      maxlength={120}
                    />
                  </div>
                  <div className="preset-modal-field">
                    <span className="preset-modal-field-label">
                      Name (long, optional)
                    </span>
                    <span className="preset-modal-field-hint">
                      Falls back to short name when blank
                    </span>
                    <IonInput
                      className="preset-text-input"
                      fill="outline"
                      aria-label="Name (long, optional)"
                      value={nameLong}
                      onIonInput={(e) => setNameLong(e.detail.value ?? "")}
                      placeholder="Falls back to short name when blank"
                      maxlength={240}
                    />
                  </div>
                </div>
              </div>
            </section>

            {/* ---- Recipients ---- */}
            <section
              className="preset-section"
              data-section-id="recipients"
              ref={(el) => (sectionRefs.current.recipients = el)}
            >
              <div className="preset-section-head">
                <h2 className="preset-section-title">Recipients</h2>
                <IonButton
                  id="preset-diff-info"
                  fill="clear"
                  size="small"
                  className="preset-diff-link"
                >
                  What is the difference?
                  <IonIcon slot="end" icon={informationCircleOutline} />
                </IonButton>
                <IonPopover trigger="preset-diff-info" triggerAction="click">
                  <div className="preset-popover-content">
                    <p>
                      <strong>Constituent groups</strong> add whole groups -
                      every current member is merged into the preset's roster,
                      and the roster reflects membership changes the next time
                      the preset is materialised.
                    </p>
                    <p>
                      <strong>Individual contacts</strong> add specific people
                      on top of the groups - use this for one-off recipients who
                      aren't in any constituent group.
                    </p>
                  </div>
                </IonPopover>
              </div>
              <div className="preset-grid-2">
                <div className="preset-col">
                  <div className="preset-modal-field">
                    <span className="preset-modal-field-label">
                      Constituent groups
                    </span>
                    <span className="preset-modal-field-hint">
                      Members of these groups are merged into the preset's
                      roster.
                    </span>
                    <ConstituentGroupPicker
                      collapsible
                      selectedIds={constituentIds}
                      onChange={setConstituentIds}
                    />
                  </div>
                </div>
                <div className="preset-col">
                  <div className="preset-modal-field">
                    <span className="preset-modal-field-label">
                      Individual contacts
                    </span>
                    <span className="preset-modal-field-hint">
                      Add specific people who should also receive the preset.
                    </span>
                    <ContactPicker
                      mode="prefixed"
                      collapsible
                      countNoun="contact"
                      selectedIds={individualIds}
                      onChange={setIndividualIds}
                      placeholder="Search staff, students, carers..."
                    />
                  </div>
                </div>
              </div>

              <div className="preset-companion-optin">
                <IonCheckbox
                  labelPlacement="end"
                  justify="start"
                  aria-label="Also create a companion parent group"
                  checked={withCompanion}
                  onIonChange={(e) => setWithCompanion(e.detail.checked)}
                >
                  Also create a companion parent group
                </IonCheckbox>
                <p className="preset-modal-field-hint">
                  Creates a second, linked preset whose members are the carers
                  of this preset's student members. It is rebuilt automatically
                  whenever this preset's membership changes, and its member list
                  starts hidden.
                </p>

                {withCompanion && (
                  <div className="preset-grid-2">
                    <div className="preset-col">
                      <div className="preset-modal-field">
                        <span className="preset-modal-field-label">
                          Parent group name (short)
                          <span className="preset-required" aria-hidden="true">
                            {" "}
                            *
                          </span>
                        </span>
                        <span className="preset-modal-field-hint">
                          Defaults to this preset's name plus "- Parents".
                        </span>
                        <IonInput
                          className="preset-text-input"
                          fill="outline"
                          aria-label="Parent group name (short)"
                          value={companionShortValue}
                          onIonInput={(e) =>
                            setCompanionNameShort(e.detail.value ?? "")
                          }
                          maxlength={PRESET_NAME_SHORT_MAX}
                        />
                      </div>
                    </div>
                    <div className="preset-col">
                      <div className="preset-modal-field">
                        <span className="preset-modal-field-label">
                          Parent group name (long)
                        </span>
                        <span className="preset-modal-field-hint">
                          Falls back to the short parent group name when blank.
                        </span>
                        <IonInput
                          className="preset-text-input"
                          fill="outline"
                          aria-label="Parent group name (long)"
                          value={companionLongValue}
                          onIonInput={(e) =>
                            setCompanionNameLong(e.detail.value ?? "")
                          }
                          maxlength={PRESET_NAME_LONG_MAX}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </section>

            {/* ---- Administration ---- */}
            <section
              className="preset-section"
              data-section-id="administration"
              ref={(el) => (sectionRefs.current.administration = el)}
            >
              <div className="preset-section-head">
                <h2 className="preset-section-title">
                  Administration{" "}
                  <span className="preset-section-optional">(optional)</span>
                </h2>
                <IonButton
                  fill="outline"
                  size="small"
                  className="preset-admin-toggle"
                  aria-expanded={adminOpen}
                  onClick={() => setAdminOpen((o) => !o)}
                >
                  {adminOpen ? "Collapse" : "Expand"}
                  <IonIcon
                    slot="end"
                    icon={chevronDownOutline}
                    className={
                      adminOpen
                        ? "preset-admin-toggle-icon open"
                        : "preset-admin-toggle-icon"
                    }
                  />
                </IonButton>
              </div>
              <p className="preset-modal-field-hint preset-admin-blurb">
                Managers can read and edit the preset (incl. materialise it),
                but cannot edit the manager list or delete the preset.
              </p>

              {adminOpen && (
                <div className="preset-modal-field">
                  <span className="preset-modal-field-label">Managers</span>
                  <span className="preset-modal-field-hint">
                    Add or remove later from the preset's detail page.
                  </span>
                  <ContactPicker
                    mode="numeric"
                    collapsible
                    countNoun="manager"
                    selectedIds={managerIds}
                    onChange={setManagerIds}
                    placeholder="Search staff..."
                    types={["staff"]}
                    unknownLabel="Manager"
                  />
                </div>
              )}

              <div className="preset-privacy-banner">
                <IonIcon icon={lockClosedOutline} aria-hidden="true" />
                <span>Privacy mode: most-restrictive (locked)</span>
              </div>
            </section>
          </div>
        </div>
      </IonContent>

      <IonFooter className="preset-create-footer">
        <IonToolbar>
          <div className="preset-modal-actions">
            <IonButton
              fill="outline"
              onClick={onDismiss}
              disabled={isSubmitting}
            >
              Cancel
            </IonButton>
            <IonButton
              color="secondary"
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Creating..." : "Create preset"}
            </IonButton>
          </div>
        </IonToolbar>
      </IonFooter>
    </>
  );
}
